import { Button } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { ProjectGroupFormModal } from '@/features/project/groups/_components/ProjectGroupFormModal';
import { useCreateGroup } from '@/features/project/groups/_hooks/group';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { useMessage } from '@/providers';
import dayjs from 'dayjs';
import { type ComponentProps, type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  buttonType?: ComponentProps<typeof Button>['type'];
};
export const GroupCreateModalButton: FC<Props> = ({ buttonType }) => {
  const me = useMe();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const [open, setOpen] = useState(false);
  const currentFiscalYear = useCurrentFiscalYear();
  const organizationIds = useRecursiveOrganizationFilteredByKey({
    params: { ids: me.data?.organizationIds ?? [] },
    enabled: !!me.data,
    options: { separator: undefined, filterBy: 'id', reverse: true },
  });

  const { mutateAsync } = useCreateGroup();

  if (currentFiscalYear.isPending || organizationIds.isPending) {
    return null;
  }

  return (
    <>
      <Button
        onClick={() => {
          setOpen(true);
        }}
        type={buttonType}
      >
        {t('create-project-group')}
      </Button>
      <ProjectGroupFormModal
        type="new"
        open={open}
        onClose={() => setOpen(false)}
        initialValues={{
          fiscalYear: dayjs().year(
            currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
          ),
          organizationIds:
            organizationIds.data?.length === 1
              ? organizationIds.data[0]
              : undefined,
        }}
        isLoading={organizationIds.isPending}
        onSubmit={async ({ ...values }) => {
          await mutateAsync({
            ...values,
            targetType: 'project',
            sharedOrganizationIds: [],
          });
          openMessage('success', t('message-create-completed'));
          setOpen(false);
        }}
      />
    </>
  );
};
